"use client";

import { DateInfo } from "@/app/components/backend/date-info";
import { ChevronDown, Loader2, MoreHorizontal } from "lucide-react";
import { createPortal } from "react-dom";
import {
  BackendButton,
  backendSelectContentClass,
  backendSelectItemClass,
} from "./backend-ui";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  formatStatus,
  getStatusColorClass,
} from "@/app/utils/helpers/format/status";
import { getPreviewUrl } from "@/app/utils/helpers/url-helpers";
import type { StatusT } from "@/app/types/status";
import { useFormContext, useWatch } from "react-hook-form";
import { useEffect, useLayoutEffect, useState } from "react";
import DirtyWarning from "@/app/components/backend/dirty-warning";
import ReadonlyWarning from "./offers/readonly-warning";
import type { ReactNode } from "react";

type StatusActionOptionT = {
  value: string;
  label: string;
  onClick: () => void;
};

type BackendStatusBarPortalTargetsT = {
  statusSlot: HTMLElement;
  actionsSlot: HTMLElement;
  detailActionsSlot: HTMLElement | null;
};

const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

const getCompanySlugFromCompanies = (
  companies: unknown
): string | undefined => {
  if (!Array.isArray(companies)) {
    return undefined;
  }

  const companySlug = companies.find(
    (company) => typeof company?.slug === "string" && company.slug.length > 0
  )?.slug;

  return typeof companySlug === "string" ? companySlug : undefined;
};

const getJobTenantKey = (tenantKey: unknown): string | undefined => {
  return typeof tenantKey === "string" && tenantKey.trim().length > 0
    ? tenantKey
    : undefined;
};

/**
 * Props for BackendStatusBar component
 *
 * Simplified props structure for better maintainability
 */
export type BackendStatusBarPropsT = {
  status?: StatusT;
  createdAt: string | null;
  updatedAt?: string;
  isSaving?: boolean;
  pendingStatus?: string | null;
  onSave?: () => void;
  onStatusChange?: (newStatus: StatusT) => void;
  showSaveButton?: boolean;
  /**
   * Label of the plain save button. Defaults to "Entwurf speichern" for
   * entities with a status (draft/publish flow) and "Speichern" otherwise
   * (e.g. channels, leads), where saving is already the final state.
   */
  saveLabel?: string;
  disabled?: boolean;
  saveDisabled?: boolean;
  publishDisabled?: boolean;
  slug?: string;
  previewTenantKey?: string | null;
  previewCompanySlug?: string | null;
  isDirty?: boolean;
  customActions?: ReactNode;
  /**
   * Which actions the publish split dropdown offers (default: both).
   * Entities without a draft flow (e.g. Benutzer) pass ["archived"].
   */
  statusActions?: Array<"archived" | "draft">;
  /**
   * Label of the publish split-button primary (default „Veröffentlichen").
   * Pages whose primary saves once published pass „Speichern" conditionally.
   */
  publishLabel?: string;
  /**
   * Whether the form is in read-only mode (disables all write operations)
   */
  isReadOnly?: boolean;
};

export function BackendStatusBar({
  status,
  createdAt,
  updatedAt,
  isSaving = false,
  pendingStatus = null,
  onSave,
  onStatusChange,
  showSaveButton = true,
  saveLabel,
  disabled = false,
  saveDisabled,
  publishDisabled,
  slug,
  previewTenantKey,
  previewCompanySlug,
  isDirty: isDirtyProp,
  customActions,
  statusActions = ["archived", "draft"],
  publishLabel = "Veröffentlichen",
  isReadOnly = false,
}: BackendStatusBarPropsT) {
  const pathname = usePathname();

  // Get isDirty from form context (BackendStatusBar is always used within FormProvider)
  // Prop takes precedence if provided
  const formContext = useFormContext();
  const isDirty = isDirtyProp ?? formContext?.formState.isDirty ?? false;
  const watchedCompanies = useWatch({
    control: formContext?.control,
    name: "companies",
  });
  const watchedTenantKey = useWatch({
    control: formContext?.control,
    name: "tenant_key",
  });

  const companySlug =
    previewCompanySlug ??
    (pathname.includes("/dashboard/angebote")
      ? getCompanySlugFromCompanies(watchedCompanies)
      : undefined);
  const jobTenantKey =
    previewTenantKey ??
    (pathname.includes("/dashboard/jobs")
      ? getJobTenantKey(watchedTenantKey)
      : undefined);

  const previewUrl = getPreviewUrl({
    slug,
    pathname,
    companySlug,
    tenantKey: jobTenantKey,
  });
  const canPreview = status === "published" && previewUrl;

  // Create split button options for dropdown (filtered per entity — users
  // for example have no draft flow and only offer "Archivieren")
  const statusActionOptions: StatusActionOptionT[] = [
    {
      value: "archived",
      label: "Archivieren",
      onClick: () => onStatusChange?.("archived"),
    },
    {
      value: "draft",
      label: "Entwurf",
      onClick: () => onStatusChange?.("draft"),
    },
  ].filter((option) =>
    statusActions.includes(option.value as "archived" | "draft")
  );

  // Handle main button click (publish action)
  const handlePublishClick = () => {
    onStatusChange?.("published");
  };

  const [portalTargets, setPortalTargets] =
    useState<BackendStatusBarPortalTargetsT | null>(null);

  useIsomorphicLayoutEffect(() => {
    const header = document.querySelector<HTMLElement>(
      '[data-backend-page-header="true"]'
    );
    const statusSlot = header?.querySelector<HTMLElement>(
      '[data-backend-page-header-status-slot="true"]'
    );
    const actionsSlot = header?.querySelector<HTMLElement>(
      '[data-backend-page-header-actions-slot="true"]'
    );
    const detailActionsSlot = document.querySelector<HTMLElement>(
      '[data-backend-detail-actions-slot="true"]'
    );

    setPortalTargets(
      statusSlot && actionsSlot
        ? { statusSlot, actionsSlot, detailActionsSlot }
        : null
    );
  }, [pathname]);

  const statusMeta = (
    <div className="flex min-w-0 flex-wrap items-center gap-3">
      {/* Status Badge */}
      {status && (
        <span
          className={cn(
            "hidden items-center rounded-md px-2 py-1 text-xs font-medium min-[1101px]:inline-flex",
            getStatusColorClass(status)
          )}
        >
          {formatStatus(status)}
        </span>
      )}
      <DateInfo createdAt={createdAt} updatedAt={updatedAt} />
    </div>
  );

  const mobileStatusBadge = status ? (
    <span
      className={cn(
        "inline-flex shrink-0 items-center rounded-md px-2 py-1 text-xs font-medium",
        getStatusColorClass(status)
      )}
    >
      {formatStatus(status)}
    </span>
  ) : null;

  const saveActionDisabled = saveDisabled ?? (disabled || !!pendingStatus);
  const resolvedSaveLabel =
    saveLabel ?? (status ? "Entwurf speichern" : "Speichern");
  const publishActionDisabled =
    publishDisabled ?? (disabled || isSaving || !!pendingStatus);

  const renderPreviewButton = () =>
    canPreview ? (
      <BackendButton asChild variant="secondary" size="md">
        <Link href={previewUrl!} target="_blank" rel="noopener noreferrer">
          Live ansehen
        </Link>
      </BackendButton>
    ) : null;

  const renderSaveButton = (isCompact = false) =>
    showSaveButton && onSave ? (
      <BackendButton
        type="button"
        variant="secondary"
        size="md"
        onClick={onSave}
        disabled={saveActionDisabled}
        className={isCompact ? "!h-9 !px-3 text-xs" : undefined}
      >
        {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
        <span>{isSaving ? "Speichert" : resolvedSaveLabel}</span>
      </BackendButton>
    ) : null;

  const renderStatusSplitButton = (isCompact = false) =>
    status && onStatusChange ? (
      <div className="flex">
        <BackendButton
          type="button"
          onClick={handlePublishClick}
          disabled={publishActionDisabled}
          size="md"
          className={cn(
            "rounded-r-none border-r border-r-white/25",
            isCompact && "!h-9 !px-3 text-xs"
          )}
        >
          {pendingStatus === "published" && (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
          <span>{publishLabel}</span>
        </BackendButton>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <BackendButton
              type="button"
              disabled={publishActionDisabled}
              size="md"
              className={cn(
                "rounded-l-none",
                isCompact ? "!h-9 !px-2.5" : "px-3"
              )}
            >
              <ChevronDown className="size-4" />
            </BackendButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className={backendSelectContentClass}
          >
            {statusActionOptions.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onClick={option.onClick}
                disabled={disabled || isSaving || !!pendingStatus}
                className={backendSelectItemClass}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    ) : null;

  const hasSaveAction = showSaveButton && !!onSave;
  const hasMobilePrimaryAction = (status && onStatusChange) || hasSaveAction;
  const hasMobileOverflowActions =
    isReadOnly ||
    isDirty ||
    !!customActions ||
    !!canPreview ||
    (hasSaveAction && !!status && !!onStatusChange);

  const actionButtons = (
    <div className="backend-status-actions hidden flex-row flex-wrap items-center justify-end gap-2 min-[1101px]:flex min-[1101px]:flex-nowrap">
      {/* Read-only Warning - shown if the form is in read-only mode due to lack of permissions */}
      {isReadOnly && <ReadonlyWarning />}
      {/* Dirty Warning - shown if there are unsaved changes in the form */}
      {isDirty && <DirtyWarning />}
      {customActions}
      {/* Preview Button - Eye icon */}
      {renderPreviewButton()}

      {/* Save Button */}
      {renderSaveButton()}

      {/* Status Split Button - only show if status is provided */}
      {renderStatusSplitButton()}
    </div>
  );

  const mobileActionBar =
    hasMobilePrimaryAction || hasMobileOverflowActions ? (
      <div className="border-t border-[var(--c-border)] bg-[var(--c-surface)] max-[1100px]:block min-[1101px]:hidden">
        <div className="mx-auto flex h-[54px] max-w-[1480px] items-center justify-between gap-3 px-4 md:px-7">
          <div className="min-w-0">{mobileStatusBadge}</div>
          <div className="flex shrink-0 items-center justify-end gap-2">
            {hasMobileOverflowActions && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <BackendButton
                    type="button"
                    variant="secondary"
                    size="md"
                    className="!h-9 !px-2.5"
                    aria-label="Weitere Aktionen"
                  >
                    <MoreHorizontal className="size-4" />
                  </BackendButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className={cn(backendSelectContentClass, "w-[250px]")}
                >
                  {(isReadOnly || isDirty) && (
                    <div className="space-y-2 border-b border-[var(--c-border-soft-2)] px-4 py-3">
                      {isReadOnly && <ReadonlyWarning />}
                      {isDirty && <DirtyWarning />}
                    </div>
                  )}

                  {customActions && (
                    <div className="border-b border-[var(--c-border-soft-2)] p-1 [&_a]:flex [&_a]:h-9 [&_a]:w-full [&_a]:items-center [&_a]:justify-start [&_a]:rounded-[8px] [&_a]:border-0 [&_a]:bg-transparent [&_a]:px-3 [&_a]:text-sm [&_a]:font-medium [&_a]:text-[var(--c-text)] [&_a]:shadow-none [&_a]:hover:bg-[var(--c-surface)] [&_button]:flex [&_button]:h-9 [&_button]:w-full [&_button]:justify-start [&_button]:rounded-[8px] [&_button]:border-0 [&_button]:bg-transparent [&_button]:px-3 [&_button]:text-sm [&_button]:font-medium [&_button]:text-[var(--c-text)] [&_button]:shadow-none [&_button]:hover:bg-[var(--c-surface)]">
                      {customActions}
                    </div>
                  )}

                  {canPreview && (
                    <DropdownMenuItem
                      asChild
                      className={backendSelectItemClass}
                    >
                      <Link
                        href={previewUrl!}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Live ansehen
                      </Link>
                    </DropdownMenuItem>
                  )}

                  {hasSaveAction && status && onStatusChange && (
                    <DropdownMenuItem
                      onSelect={(event) => {
                        event.preventDefault();
                        onSave?.();
                      }}
                      disabled={saveActionDisabled}
                      className={backendSelectItemClass}
                    >
                      {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                      {isSaving ? "Speichert" : "Entwurf speichern"}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {status && onStatusChange
              ? renderStatusSplitButton(true)
              : renderSaveButton(true)}
          </div>
        </div>
      </div>
    ) : null;

  if (portalTargets) {
    return (
      <>
        {createPortal(statusMeta, portalTargets.statusSlot)}
        {createPortal(actionButtons, portalTargets.actionsSlot)}
        {portalTargets.detailActionsSlot && mobileActionBar
          ? createPortal(mobileActionBar, portalTargets.detailActionsSlot)
          : null}
      </>
    );
  }

  return (
    <div className="backend-status-bar mb-6 text-[13px]">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        {statusMeta}
        {actionButtons}
      </div>
      {mobileActionBar}
    </div>
  );
}
